import { computed, type ComputedRef } from "vue";
import type {
    PhieuXuatKhoSuppliesSource,
    PhieuXuatKhoVatTuTableMode,
} from "../types";

export type PhieuXuatKhoTableColumnId =
  | "stt"
  | "ten_vat_tu"
  | "ma_vat_tu"
  | "don_vi_tinh"
  | "ton_kho"
  | "so_luong_xuat"
  | "sl_dinh_muc"
  | "thu_hoi"
  | "vat_tu_xuat_kho"
  | "don_gia"
  | "thanh_tien"
  | "ghi_chu"
  | "delete";

/** Leaf columns under the `vat_tu_xuat_kho` group header. */
export type PhieuXuatKhoXuatKhoLeafColumnId =
  | "so_luong_xuat"
  | "don_gia"
  | "thanh_tien";

export interface PhieuXuatKhoTableColumnContext {
  mode: PhieuXuatKhoVatTuTableMode;
  source: PhieuXuatKhoSuppliesSource;
  readonly: boolean;
  showThuHoiColumns: boolean;
  showLyDoColumn: boolean;
  showDeleteAction: boolean;
  showTonKhoColumn: boolean;
  hasSupplies: boolean;
}

export const THU_HOI_GROUP_LABEL = "Vật tư phụ tùng phải thu hồi";
export const XUAT_KHO_GROUP_LABEL = "Vật tư xuất kho";

/** Nested columns under `thu_hoi` — `ma_vt_thu_hoi` renamed to avoid clashing with `ma_vat_tu`. */
export const THU_HOI_SUB_COLUMNS = [
  { id: "tai_sd", label: "Tái SD" },
  { id: "mat", label: "Mất" },
  { id: "so_luong_phe_lieu", label: "Số Lg", parentLabel: "Phế liệu" },
  { id: "quy_doi_kg", label: "Quy đổi (Kg)", parentLabel: "Phế liệu" },
  { id: "ma_vt_thu_hoi", label: "MVT thu hồi", parentLabel: "Phế liệu" },
] as const;

export const XUAT_KHO_LEAF_COLUMNS = [
  { id: "so_luong_xuat", label: "SL xuất" },
  { id: "don_gia", label: "Đơn giá" },
  { id: "thanh_tien", label: "Thành tiền" },
] as const satisfies ReadonlyArray<{
  id: PhieuXuatKhoXuatKhoLeafColumnId;
  label: string;
}>;

export const THU_HOI_LEAF_COLSPAN = 5;
export const XUAT_KHO_LEAF_COLSPAN = XUAT_KHO_LEAF_COLUMNS.length;

interface ColumnDefinition {
  label: string | ((ctx: PhieuXuatKhoTableColumnContext) => string);
  colspan: number | ((ctx: PhieuXuatKhoTableColumnContext) => number);
  visible: (ctx: PhieuXuatKhoTableColumnContext) => boolean;
  footerRole?: "label" | "don_gia" | "value";
  fixed?:
    | "left"
    | "right"
    | ((ctx: PhieuXuatKhoTableColumnContext) => "left" | "right" | undefined);
  summaryWidth?: number;
  /** Fixed pick-mode width — column does not absorb leftover table space. */
  pickWidth?: number;
  /**
   * Flexible pick-mode min-width — use without `pickWidth` so the column
   * expands on ultra-wide screens (priority: SL xuất / Đơn giá / Thành tiền).
   */
  pickMinWidth?: number;
  align?: "left" | "right" | "center";
  pickHeaderBold?: boolean;
  showOverflowTooltip?: boolean;
}

export interface PhieuXuatKhoColumnLayout {
  align?: "left" | "right" | "center";
  fixed?: "left" | "right";
  pickHeaderBold?: boolean;
  pickMinWidth?: number;
  pickWidth?: number;
  showOverflowTooltip?: boolean;
  summaryWidth?: number;
}

export function getColumnFixed(
  id: PhieuXuatKhoTableColumnId,
  ctx: PhieuXuatKhoTableColumnContext
): "left" | "right" | undefined {
  const fixed = PHIEU_XUAT_KHO_TABLE_COLUMNS[id].fixed;

  if (!fixed) {
    return undefined;
  }

  return typeof fixed === "function" ? fixed(ctx) : fixed;
}

export function getColumnLayout(
  id: PhieuXuatKhoTableColumnId
): PhieuXuatKhoColumnLayout {
  const def = PHIEU_XUAT_KHO_TABLE_COLUMNS[id];

  return {
    summaryWidth: def.summaryWidth,
    pickWidth: def.pickWidth,
    pickMinWidth: def.pickMinWidth,
    align: def.align,
    pickHeaderBold: def.pickHeaderBold,
    showOverflowTooltip: def.showOverflowTooltip,
  };
}

export function getThuHoiLeafColspan(): number {
  return THU_HOI_LEAF_COLSPAN;
}

export function getXuatKhoLeafColspan(): number {
  return XUAT_KHO_LEAF_COLSPAN;
}

/** Total width of the fixed `Vật tư xuất kho` group (required by EP 1.2). */
export function getXuatKhoGroupWidth(isSummaryMode: boolean): number {
  return XUAT_KHO_LEAF_COLUMNS.reduce((total, leaf) => {
    const layout = getColumnLayout(leaf.id);

    if (isSummaryMode) {
      return total + (layout.summaryWidth ?? layout.pickWidth ?? 80);
    }

    return total + (layout.pickWidth ?? layout.summaryWidth ?? 80);
  }, 0);
}

export interface PhieuXuatKhoResolvedColumnBindings {
  align?: "left" | "right" | "center";
  fixed?: "left" | "right";
  label: string;
  minWidth?: number;
  pickHeaderBold?: boolean;
  showOverflowTooltip?: boolean;
  width?: number;
}

/** Resolve el-table-column width/align bindings for a leaf column. */
export function resolveColumnBindings(
  columnId: PhieuXuatKhoTableColumnId,
  ctx: PhieuXuatKhoTableColumnContext,
  isSummaryMode: boolean
): PhieuXuatKhoResolvedColumnBindings {
  const layout = getColumnLayout(columnId);
  const label = getColumnLabel(columnId, ctx);
  const fixed = getColumnFixed(columnId, ctx);
  const isXuatKhoLeaf =
    columnId === "so_luong_xuat" ||
    columnId === "don_gia" ||
    columnId === "thanh_tien";

  if (isSummaryMode) {
    // Fixed group leaves need explicit width (not flex min-width).
    if (isXuatKhoLeaf) {
      return {
        align: layout.align,
        fixed,
        label,
        pickHeaderBold: layout.pickHeaderBold,
        showOverflowTooltip: false,
        width: layout.summaryWidth ?? layout.pickWidth,
      };
    }

    return {
      align: layout.align,
      fixed,
      label,
      minWidth: undefined,
      pickHeaderBold: layout.pickHeaderBold,
      showOverflowTooltip: false,
      width: layout.summaryWidth,
    };
  }

  // Fixed group leaves: always use explicit width for EP fixed panel.
  if (isXuatKhoLeaf) {
    return {
      align: layout.align,
      fixed,
      label,
      pickHeaderBold: layout.pickHeaderBold,
      showOverflowTooltip: layout.showOverflowTooltip,
      width: layout.pickWidth ?? layout.summaryWidth,
    };
  }

  const isFlexColumn = layout.pickMinWidth != null && layout.pickWidth == null;

  return {
    align: layout.align,
    fixed,
    label,
    minWidth: isFlexColumn ? layout.pickMinWidth : undefined,
    pickHeaderBold: layout.pickHeaderBold,
    showOverflowTooltip: layout.showOverflowTooltip,
    width: isFlexColumn ? undefined : layout.pickWidth ?? layout.pickMinWidth,
  };
}

export function getColumnColspan(
  id: PhieuXuatKhoTableColumnId,
  ctx: PhieuXuatKhoTableColumnContext
): number {
  const def = PHIEU_XUAT_KHO_TABLE_COLUMNS[id];
  const colspan = def.colspan;

  if (typeof colspan === "function") {
    return colspan(ctx);
  }

  return colspan;
}

export const PHIEU_XUAT_KHO_TABLE_COLUMNS: Record<
  PhieuXuatKhoTableColumnId,
  ColumnDefinition
> = {
  stt: {
    label: "STT",
    colspan: 1,
    visible: () => true,
    summaryWidth: 50,
    pickWidth: 50,
  },
  ten_vat_tu: {
    label: "Tên vật tư",
    colspan: 1,
    visible: () => true,
    summaryWidth: 200,
    pickWidth: 200,
    showOverflowTooltip: true,
  },
  ma_vat_tu: {
    label: "MVT",
    colspan: 1,
    visible: () => true,
    summaryWidth: 100,
    pickWidth: 80,
    showOverflowTooltip: true,
  },
  don_vi_tinh: {
    label: "ĐVT",
    colspan: 1,
    visible: () => true,
    summaryWidth: 60,
    pickWidth: 80,
    showOverflowTooltip: true,
  },
  ton_kho: {
    label: (ctx) => (ctx.mode === "pick" ? "SL tồn" : "Tồn còn lại"),
    colspan: 1,
    visible: (ctx) => (ctx.mode === "pick" ? ctx.showTonKhoColumn : true),
    summaryWidth: 100,
    pickWidth: 90,
    align: "right",
    showOverflowTooltip: true,
  },
  so_luong_xuat: {
    label: "SL xuất",
    colspan: 1,
    visible: () => true,
    footerRole: "label",
    // Fits formatQuantity max 8 integer digits: "12,345,678.00"
    summaryWidth: 120,
    pickWidth: 120,
    align: "right",
    showOverflowTooltip: true,
  },
  sl_dinh_muc: {
    label: "SL định mức",
    colspan: 1,
    visible: (ctx) => ctx.mode === "pick" && ctx.source === "bbgt",
    pickWidth: 110,
    align: "right",
    showOverflowTooltip: true,
  },
  thu_hoi: {
    label: THU_HOI_GROUP_LABEL,
    colspan: getThuHoiLeafColspan,
    visible: (ctx) => ctx.showThuHoiColumns,
  },
  vat_tu_xuat_kho: {
    label: XUAT_KHO_GROUP_LABEL,
    colspan: getXuatKhoLeafColspan,
    visible: () => true,
    fixed: "right",
  },
  don_gia: {
    label: "Đơn giá",
    colspan: 1,
    visible: () => true,
    footerRole: "don_gia",
    summaryWidth: 120,
    pickWidth: 120,
    align: "right",
    pickHeaderBold: true,
    showOverflowTooltip: true,
  },
  thanh_tien: {
    label: "Thành tiền",
    colspan: 1,
    visible: () => true,
    footerRole: "value",
    summaryWidth: 180,
    pickWidth: 150,
    align: "right",
    showOverflowTooltip: true,
  },
  ghi_chu: {
    label: "Lý do",
    colspan: 1,
    visible: (ctx) => ctx.showLyDoColumn,
    summaryWidth: 250,
    pickWidth: 300,
    align: "left",
    pickHeaderBold: true,
  },
  delete: {
    label: "",
    colspan: 1,
    visible: (ctx) =>
      ctx.mode === "pick" && ctx.showDeleteAction && ctx.hasSupplies,
    fixed: "right",
    pickWidth: 50,
    align: "center",
  },
};

export const SUMMARY_COLUMN_ORDER: PhieuXuatKhoTableColumnId[] = [
  "stt",
  "ten_vat_tu",
  "ma_vat_tu",
  "don_vi_tinh",
  "ton_kho",
  "thu_hoi",
  "ghi_chu",
  "vat_tu_xuat_kho",
];

export const PICK_COLUMN_ORDER: PhieuXuatKhoTableColumnId[] = [
  "stt",
  "ten_vat_tu",
  "ma_vat_tu",
  "don_vi_tinh",
  "ton_kho",
  "thu_hoi",
  "sl_dinh_muc",
  "ghi_chu",
  "vat_tu_xuat_kho",
  "delete",
];

export function getVisibleColumns(
  order: PhieuXuatKhoTableColumnId[],
  ctx: PhieuXuatKhoTableColumnContext
): PhieuXuatKhoTableColumnId[] {
  return order.filter((id) => PHIEU_XUAT_KHO_TABLE_COLUMNS[id].visible(ctx));
}

export function getColumnLabel(
  id: PhieuXuatKhoTableColumnId,
  ctx: PhieuXuatKhoTableColumnContext
): string {
  const def = PHIEU_XUAT_KHO_TABLE_COLUMNS[id];
  const label = def.label;

  return typeof label === "function" ? label(ctx) : label;
}

export function getTableColspan(
  columnIds: PhieuXuatKhoTableColumnId[],
  ctx: PhieuXuatKhoTableColumnContext
): number {
  return columnIds.reduce((total, id) => total + getColumnColspan(id, ctx), 0);
}

export function getVisibleTableColspan(
  order: PhieuXuatKhoTableColumnId[],
  ctx: PhieuXuatKhoTableColumnContext
): number {
  return getTableColspan(getVisibleColumns(order, ctx), ctx);
}

export function getFooterColumnIndexes(
  columnIds: PhieuXuatKhoTableColumnId[],
  ctx: PhieuXuatKhoTableColumnContext
) {
  let index = 0;
  let labelColumnIndex = -1;
  let donGiaColumnIndex = -1;
  let valueColumnIndex = -1;

  for (const id of columnIds) {
    if (id === "vat_tu_xuat_kho") {
      for (const leaf of XUAT_KHO_LEAF_COLUMNS) {
        const leafDef = PHIEU_XUAT_KHO_TABLE_COLUMNS[leaf.id];

        if (leafDef.footerRole === "label") {
          labelColumnIndex = index;
        }

        if (leafDef.footerRole === "don_gia") {
          donGiaColumnIndex = index;
        }

        if (leafDef.footerRole === "value") {
          valueColumnIndex = index;
        }

        index += 1;
      }

      continue;
    }

    const def = PHIEU_XUAT_KHO_TABLE_COLUMNS[id];

    if (def.footerRole === "label") {
      labelColumnIndex = index;
    }

    if (def.footerRole === "don_gia") {
      donGiaColumnIndex = index;
    }

    if (def.footerRole === "value") {
      valueColumnIndex = index;
    }

    index += getColumnColspan(id, ctx);
  }

  return {
    donGiaColumnIndex,
    labelColumnIndex,
    scrollableColspan: labelColumnIndex,
    valueColumnIndex,
  };
}

export function usePhieuXuatKhoTableColumns(
  ctx: ComputedRef<PhieuXuatKhoTableColumnContext>
) {
  const visibleSummaryColumns = computed(() =>
    getVisibleColumns(SUMMARY_COLUMN_ORDER, ctx.value)
  );

  const visiblePickColumns = computed(() =>
    getVisibleColumns(PICK_COLUMN_ORDER, ctx.value)
  );

  const summaryColspan = computed(() =>
    getTableColspan(visibleSummaryColumns.value, ctx.value)
  );

  const pickColspan = computed(() =>
    getTableColspan(visiblePickColumns.value, ctx.value)
  );

  const summaryFooterIndexes = computed(() =>
    getFooterColumnIndexes(visibleSummaryColumns.value, ctx.value)
  );

  const pickFooterIndexes = computed(() =>
    getFooterColumnIndexes(visiblePickColumns.value, ctx.value)
  );

  const visibleColumns = computed(() =>
    ctx.value.mode === "pick"
      ? visiblePickColumns.value
      : visibleSummaryColumns.value
  );

  const footerIndexes = computed(() =>
    ctx.value.mode === "pick"
      ? pickFooterIndexes.value
      : summaryFooterIndexes.value
  );

  const columnLabel = (id: PhieuXuatKhoTableColumnId) =>
    getColumnLabel(id, ctx.value);

  const isColumnVisible = (id: PhieuXuatKhoTableColumnId) =>
    PHIEU_XUAT_KHO_TABLE_COLUMNS[id].visible(ctx.value);

  return {
    columnLabel,
    footerIndexes,
    isColumnVisible,
    pickColspan,
    pickFooterIndexes,
    summaryColspan,
    summaryFooterIndexes,
    visibleColumns,
    visiblePickColumns,
    visibleSummaryColumns,
  };
}
